// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAttributionReport } from '../../../api/attributionApi';
import { listTeamMembers } from '../../../api/jobsApi';
import { listTechnicians } from '../../../api/schedulingApi';
import { getActiveTenant } from '../../../helpers/tenantHelpers';
import './analytics-marketing.css';

/* Analytics → Marketing — "Which channels made money."
   Contract: docs/MARKETING_REPORT_FRONTEND_CONTRACT.md (lane E); UI asks in
   docs/MARKETING_REPORT_UI_ASKS_2026-09-08.md (rep per row, technician filter
   echo, subscription value). Copy marked VERBATIM in E's contract is kept
   verbatim — it now lives in the ⓘ tooltip, with one visible line.
   Filters: date presets + custom range; campaign / rep / technician as
   dropdowns from real data (the campaigns in the rows, the team, the roster).
   Everything applies on change; the whole page re-queries live. */

const HONEST_LIMIT =
  'For someone who came from a ChatGPT ad we know the campaign and landing page — we do not receive their conversation with ChatGPT, and never will. Anonymous visitors who don’t become leads are counted, never identified.';

const KNOWN_CHANNELS = [
  { key: 'google', label: 'Google Ads' },
  { key: 'meta', label: 'Meta Ads' },
  { key: 'openai', label: 'ChatGPT Ads' },
];

const PRESETS = [
  { key: '7d', label: '7d' }, { key: '30d', label: '30d' }, { key: '90d', label: '90d' },
  { key: 'this_month', label: 'This month' }, { key: 'last_month', label: 'Last month' }, { key: 'custom', label: 'Custom' },
];
const iso = (d) => d.toISOString().slice(0, 10);
const rangeFor = (preset) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const daysAgo = (n) => { const d = new Date(today); d.setDate(d.getDate() - n); return d; };
  switch (preset) {
    case '7d': return { start: iso(daysAgo(7)), end: iso(today) };
    case '30d': return { start: iso(daysAgo(30)), end: iso(today) };
    case '90d': return { start: iso(daysAgo(90)), end: iso(today) };
    case 'this_month': return { start: iso(new Date(now.getFullYear(), now.getMonth(), 1)), end: iso(today) };
    case 'last_month': return { start: iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)), end: iso(new Date(now.getFullYear(), now.getMonth(), 0)) };
    default: return null;
  }
};

const money = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v); if (isNaN(n)) return String(v);
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
};
const num = (v) => (v == null ? '—' : Number(v).toLocaleString());
const fmtTime = (v) => { if (!v) return ''; try { return new Date(v).toLocaleString(); } catch { return v; } };
const fmtDay = (v) => { if (!v) return ''; try { return new Date(v + (String(v).length === 10 ? 'T00:00:00' : '')).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); } catch { return v; } };
const personName = (m) => [m.first_name, m.last_name].filter(Boolean).join(' ').trim() || m.name || m.full_name || m.email || `#${m.id}`;

function Tile({ label, value, sub, muted }) {
  return (
    <div className={`mk-tile ${muted ? 'is-muted' : ''}`}>
      <div className="mk-tile-label">{label}</div>
      <div className="mk-tile-value">{value}</div>
      {sub ? <div className="mk-tile-sub">{sub}</div> : null}
    </div>
  );
}
function Chip({ cls, children, title }) { return <span className={`mk-chip ${cls || ''}`} title={title}>{children}</span>; }

/* Search performance — Search Console + GA4 blocks on the report payload
   (docs/WEB_VISIBILITY_CONTRACTS_2026-09-22.md §7). Rendered only when the
   backend emits the keys; `not_connected` shows the backend's steps verbatim
   with the service-account email copyable. Never a placeholder number. */
const pct = (v) => (v == null || v === '' ? '—' : `${(Number(v) * (Number(v) <= 1 ? 100 : 1)).toFixed(1)}%`);
const pos = (v) => (v == null || v === '' ? '—' : Number(v).toFixed(1));

function CopyEmail({ email }) {
  const [copied, setCopied] = useState(false);
  if (!email) return null;
  const copy = async () => { try { await navigator.clipboard.writeText(email); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard unavailable — the text is selectable */ } };
  return (
    <span className="mk-copy">
      <code>{email}</code>
      <button type="button" className="mk-btn" onClick={copy} aria-label="Copy service-account email">{copied ? 'Copied' : 'Copy'}</button>
    </span>
  );
}

function NotConnected({ block, label }) {
  const steps = block?.steps;
  return (
    <div className="mk-note">
      <strong>{label} isn’t connected.</strong>
      {Array.isArray(steps) ? (
        <ol className="mk-steps">{steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
      ) : steps ? (
        <div className="mk-steps-text">{String(steps)}</div>
      ) : null}
      {block?.service_account_email && <div className="mk-steps-text">Grant read access to: <CopyEmail email={block.service_account_email} /></div>}
      {block?.error && <div className="mk-dim">{String(block.error)}</div>}
    </div>
  );
}

function SearchPerformance({ gsc, ga4 }) {
  if (!gsc && !ga4) return null;
  const gt = gsc?.totals || {};
  const at = ga4?.totals || {};
  const gscOk = gsc && gsc.status === 'connected';
  const ga4Ok = ga4 && ga4.status === 'connected';
  return (
    <div className="mk-card">
      <div className="mk-card-title">
        Search performance
        <span className="mk-dim">
          {[gsc?.property && gscOk ? gsc.property : null, ga4?.property && ga4Ok ? `GA4 ${ga4.property}` : null].filter(Boolean).join(' · ') || 'Search Console + GA4'}
          {(gsc?.pulled_at || ga4?.pulled_at) ? ` · pulled ${fmtTime(gsc?.pulled_at || ga4?.pulled_at)}` : ''}
        </span>
      </div>
      {gsc && (gscOk ? (
        <div className="mk-tiles" style={{ marginBottom: 12 }}>
          <Tile label="Clicks" value={num(gt.clicks)} sub="Search Console" />
          <Tile label="Impressions" value={num(gt.impressions)} />
          <Tile label="CTR" value={pct(gt.ctr)} />
          <Tile label="Avg position" value={pos(gt.position)} />
        </div>
      ) : <NotConnected block={gsc} label="Search Console" />)}
      {ga4 && (ga4Ok ? (
        <div className="mk-tiles" style={{ marginBottom: 12 }}>
          <Tile label="Sessions" value={num(at.sessions)} sub="GA4" />
          <Tile label="Users" value={num(at.users)} />
          <Tile label="Engaged sessions" value={num(at.engaged_sessions)} muted={at.engaged_sessions == null} />
          <Tile label="Conversions" value={num(at.conversions)} />
        </div>
      ) : <NotConnected block={ga4} label="GA4" />)}
      {gscOk && ((gsc.top_queries?.length || 0) > 0 || (gsc.top_pages?.length || 0) > 0) && (
        <div className="mk-two">
          {gsc.top_queries?.length > 0 && (
            <div className="mk-table-wrap">
              <table className="mk-table">
                <thead><tr><th>Top queries</th><th className="num">Clicks</th><th className="num">Impr.</th><th className="num">Pos.</th></tr></thead>
                <tbody>{gsc.top_queries.slice(0, 10).map((q, i) => (
                  <tr key={i}><td>{q.key || q.query}</td><td className="num">{num(q.clicks)}</td><td className="num">{num(q.impressions)}</td><td className="num">{pos(q.position)}</td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
          {gsc.top_pages?.length > 0 && (
            <div className="mk-table-wrap">
              <table className="mk-table">
                <thead><tr><th>Top pages</th><th className="num">Clicks</th><th className="num">Impr.</th><th className="num">Pos.</th></tr></thead>
                <tbody>{gsc.top_pages.slice(0, 10).map((p, i) => (
                  <tr key={i}><td className="mk-path" title={p.key || p.page}>{(p.key || p.page || '').replace(/^https?:\/\/[^/]+/, '') || '/'}</td><td className="num">{num(p.clicks)}</td><td className="num">{num(p.impressions)}</td><td className="num">{pos(p.position)}</td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* Who sold it: rows carry `reps` once E ships it (docs/MARKETING_REPORT_UI_ASKS). */
function SoldBy({ reps }) {
  if (!Array.isArray(reps) || reps.length === 0) return <span className="mk-dim" title="Per-row rep arrives with the backend (lane E)">—</span>;
  const sorted = [...reps].sort((a, b) => Number(b.revenue || 0) - Number(a.revenue || 0) || Number(b.closed_won || 0) - Number(a.closed_won || 0));
  const top = sorted[0];
  const rest = sorted.length - 1;
  return (
    <span title={sorted.map((r) => `${r.name || r.user_id}: ${Number(r.closed_won || 0)} won${r.revenue != null ? ` · ${money(r.revenue)}` : ''}`).join('\n')}>
      {top.name || `#${top.user_id}`}{rest > 0 ? <span className="mk-dim"> +{rest}</span> : null}
    </span>
  );
}

export default function AnalyticsMarketing() {
  const navigate = useNavigate();
  const tenant = getActiveTenant() || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notDeployed, setNotDeployed] = useState(false);
  const [error, setError] = useState(null);
  const [preset, setPreset] = useState('30d');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [filters, setFilters] = useState({ campaign: '', tech: '', technician: '', service: '', landing_path: '' });
  const [more, setMore] = useState(false);
  const [open, setOpen] = useState({});
  const [infoOpen, setInfoOpen] = useState(false);
  const [team, setTeam] = useState([]);
  const [techs, setTechs] = useState([]);
  const [campaignOptions, setCampaignOptions] = useState([]);

  const range = useMemo(() => (preset === 'custom' ? custom : rangeFor(preset)), [preset, custom]);

  // Dropdown sources: the team (reps who can sell), the technician roster, and the campaigns
  // that actually appear in this tenant's attribution rows (fetched once, unfiltered by campaign).
  useEffect(() => {
    listTeamMembers().then((r) => setTeam(Array.isArray(r.data) ? r.data : r.data?.results || r.data?.team || [])).catch(() => {});
    listTechnicians().then((r) => setTechs(Array.isArray(r.data) ? r.data : r.data?.results || [])).catch(() => {});
    getAttributionReport({}).then((r) => {
      const seen = new Map();
      (r.data?.rows || []).forEach((row) => { const c = row.campaign; if (c && c !== '(none)' && !seen.has(c)) seen.set(c, row.channel_label || row.channel); });
      setCampaignOptions([...seen.entries()].map(([value, channel]) => ({ value, channel })).sort((a, b) => a.value.localeCompare(b.value)));
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null); setNotDeployed(false);
    const params = { ...filters };
    if (range?.start) params.start = range.start;
    if (range?.end) params.end = range.end;
    const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null));
    try { const { data: d } = await getAttributionReport(clean); setData(d); }
    catch (e) { if (e?.status === 404) setNotDeployed(true); else setError(e?.message || 'Couldn’t load the report.'); }
    finally { setLoading(false); }
  }, [filters, range]);
  useEffect(() => { if (preset !== 'custom' || (custom.start && custom.end)) load(); }, [load, preset, custom]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearAll = () => { setFilters({ campaign: '', tech: '', technician: '', service: '', landing_path: '' }); setPreset('30d'); setCustom({ start: '', end: '' }); };
  const activeCount = Object.values(filters).filter(Boolean).length;

  // Backend echo of applied filters (E's contract: filters.{service,campaign,landing_path,tech_id}).
  // A technician filter is honest only when the backend says it applied it.
  const techApplied = data?.filters && ('technician_id' in data.filters || 'technician' in data.filters);
  const techPending = !!filters.technician && data && !techApplied;

  const rows = useMemo(() => data?.rows || [], [data]);
  const subscriptionBasis = useMemo(() => {
    const basis = String(data?.revenue_basis || '').toLowerCase();
    return basis.includes('subscription') || rows.some((r) => r.subscription_value != null);
  }, [data, rows]);

  const channels = useMemo(() => {
    const tied = data?.tied_accounts || {};
    const byKey = new Map();
    const add = (key, label) => { if (!byKey.has(key)) byKey.set(key, { key, label, rows: [], leads: 0, booked: 0, revenue: 0, subscription: 0, cost: null, precision: 'high', basis: '', reps: new Map() }); return byKey.get(key); };
    for (const r of rows) {
      const g = add(r.channel, r.channel_label || r.channel);
      g.rows.push(r);
      g.leads += Number(r.leads || 0); g.booked += Number(r.booked_appointments || 0); g.revenue += Number(r.revenue || 0);
      if (r.subscription_value != null) g.subscription += Number(r.subscription_value || 0);
      const c = r.cost ?? r.spend; if (c != null) g.cost = (g.cost || 0) + Number(c);
      if (r.precision === 'low' || (r.precision === 'medium' && g.precision !== 'low')) g.precision = r.precision;
      if (!g.basis && r.attribution_basis) g.basis = r.attribution_basis;
      (r.reps || []).forEach((rep) => {
        const k = rep.user_id ?? rep.name; const cur = g.reps.get(k) || { user_id: rep.user_id, name: rep.name, closed_won: 0, revenue: 0 };
        cur.closed_won += Number(rep.closed_won || 0); cur.revenue += Number(rep.revenue || 0); g.reps.set(k, cur);
      });
    }
    for (const k of KNOWN_CHANNELS) add(k.key, k.label);
    for (const k of Object.keys(tied)) add(k, KNOWN_CHANNELS.find((x) => x.key === k)?.label || k);
    const list = [...byKey.values()].map((g) => ({ ...g, reps: [...g.reps.values()], tied: tied[g.key] || null, connected: tied[g.key]?.status === 'connected' }));
    const primary = (g) => (subscriptionBasis ? g.subscription : g.revenue);
    list.sort((a, b) => primary(b) - primary(a) || b.leads - a.leads || a.label.localeCompare(b.label));
    return list;
  }, [rows, data, subscriptionBasis]);

  const openLeads = (ids) => { if (ids?.length) navigate(`/leads/list?ids=${encodeURIComponent(ids.join(','))}`); };
  // Channels with rows or a tied account are the table; the rest is one quiet line, not empty rows.
  const shown = useMemo(() => channels.filter((c) => c.rows.length > 0 || c.connected || c.tied), [channels]);
  const offChannels = useMemo(() => channels.filter((c) => !(c.rows.length > 0 || c.connected || c.tied)), [channels]);

  const t = data?.totals || {};
  const adSpend = t.ad_spend ?? t.spend ?? t.cost;
  const anyConnected = channels.some((c) => c.connected);
  const reps = useMemo(() => {
    const salesish = (m) => /sales|rep|owner|admin|manager/i.test(String(m.role || ''));
    return [...team].sort((a, b) => Number(salesish(b)) - Number(salesish(a)) || personName(a).localeCompare(personName(b)));
  }, [team]);
  const tooltipLines = [data?.capture_note, data?.revenue_note, data?.excluded_note, HONEST_LIMIT].filter(Boolean);
  const primaryLabel = subscriptionBasis ? 'Subscription value' : 'Revenue';

  return (
    <div className="mk">
      {/* ── Header: title, one short line, the rest behind ⓘ ── */}
      <div className="mk-head">
        <div>
          <div className="mk-eyebrow">Marketing</div>
          <h1 className="mk-title">Which channels made money</h1>
          <div className="mk-sub">
            <span className="mk-oneline">
              {tenant.name || 'Your workspace'} · observed attribution only; inferred rows are marked
              <button type="button" className="mk-info" aria-label="How this report attributes revenue" onClick={() => setInfoOpen((o) => !o)} onBlur={() => setInfoOpen(false)}>ⓘ</button>
              {infoOpen && tooltipLines.length > 0 && (
                <span className="mk-info-pop" role="tooltip">{tooltipLines.map((l, i) => <span key={i}>{l}</span>)}</span>
              )}
            </span>
          </div>
        </div>
        {data?.generated_at && (
          <div className="mk-live" title={data.live_note || ''}><span className="mk-live-dot" aria-hidden />Live · {fmtTime(data.generated_at)}</div>
        )}
      </div>

      {/* ── Filters: presets + dropdowns from real data; apply on change ── */}
      <div className="mk-filters">
        {range?.start && <Chip cls="quiet">{fmtDay(range.start)} → {range.end ? fmtDay(range.end) : 'now'}</Chip>}
        {activeCount > 0 && <Chip cls="accent">{activeCount} filter{activeCount === 1 ? '' : 's'}</Chip>}
        <div className="mk-presets" role="tablist" aria-label="Date range">
          {PRESETS.map((p) => (
            <button key={p.key} type="button" role="tab" aria-selected={preset === p.key} className={`mk-preset ${preset === p.key ? 'is-on' : ''}`} onClick={() => setPreset(p.key)}>{p.label}</button>
          ))}
        </div>
        {preset === 'custom' && (
          <div className="mk-custom">
            <input type="date" value={custom.start} max={custom.end || undefined} onChange={(e) => setCustom({ ...custom, start: e.target.value })} aria-label="Start" />
            <span className="mk-dim">to</span>
            <input type="date" value={custom.end} min={custom.start || undefined} onChange={(e) => setCustom({ ...custom, end: e.target.value })} aria-label="End" />
          </div>
        )}
        <select value={filters.campaign} onChange={(e) => setF('campaign', e.target.value)} aria-label="Campaign">
          <option value="">Campaign: all</option>
          {campaignOptions.map((c) => <option key={c.value} value={c.value}>{c.value}{c.channel ? ` · ${c.channel}` : ''}</option>)}
          {filters.campaign && !campaignOptions.some((c) => c.value === filters.campaign) && <option value={filters.campaign}>{filters.campaign}</option>}
        </select>
        <select value={filters.tech} onChange={(e) => setF('tech', e.target.value)} aria-label="Sold by">
          <option value="">Sold by: anyone</option>
          {reps.map((m) => <option key={m.id} value={m.id}>{personName(m)}{m.role ? ` · ${String(m.role).replace(/_/g, ' ')}` : ''}</option>)}
        </select>
        <select value={filters.technician} onChange={(e) => setF('technician', e.target.value)} aria-label="Technician" title={techPending ? 'The backend does not apply a technician filter yet (lane E)' : ''}>
          <option value="">Technician: any</option>
          {techs.map((x) => <option key={x.id} value={x.id}>{x.name || x.full_name || x.user_name || x.display_name || `Tech #${x.id}`}</option>)}
        </select>
        <button type="button" className="mk-btn" onClick={() => setMore((m) => !m)}>{more ? 'Less' : 'More'}</button>
        {(activeCount > 0 || preset !== '30d') && <button type="button" className="mk-btn" onClick={clearAll}>Reset</button>}
        {more && (
          <div className="mk-more">
            <input type="text" placeholder="Service (e.g. termite)" value={filters.service} onChange={(e) => setF('service', e.target.value)} />
            <input type="text" placeholder="Landing page (e.g. /termite)" value={filters.landing_path} onChange={(e) => setF('landing_path', e.target.value)} />
          </div>
        )}
      </div>
      {techPending && <div className="mk-note warn">The technician filter isn’t applied by the backend yet — results below ignore it. Coming with lane E’s classification fix.</div>}

      {/* ── States ── */}
      {loading && !data && <div className="mk-muted">Computing live…</div>}
      {notDeployed && (
        <div className="mk-note warn"><strong>The attribution report isn’t deployed on the backend yet.</strong> This page is wired to <code>/api/analytics/attribution-report/</code> and populates on the next backend deploy — nothing here is sample data.</div>
      )}
      {error && <div className="mk-note danger">{error} <button className="mk-link" onClick={load}>Retry</button></div>}

      {data && (
        <>
          <div className="mk-tiles">
            <Tile label="Acquisition leads" value={num(t.acquisition_leads ?? t.leads)} sub={t.unattributed_leads != null ? `${num(t.unattributed_leads)} unattributed (${t.unattributed_share_pct ?? 0}%)` : null} />
            <Tile label="Booked appointments" value={num(t.booked_appointments)} sub={t.booked_leads != null ? `${num(t.booked_leads)} leads booked` : null} />
            {subscriptionBasis
              ? <Tile label="Subscription value" value={money(t.subscription_value ?? channels.reduce((s, c) => s + c.subscription, 0))} sub={t.revenue != null ? `${money(t.revenue)} booked revenue` : null} />
              : <Tile label="Booked revenue" value={money(t.revenue)} sub={data.revenue_basis ? data.revenue_basis.replace(/_/g, ' ') : null} />}
            <Tile label="Ad spend" value={adSpend != null ? money(adSpend) : '—'} sub={adSpend == null ? (anyConnected ? 'no spend reported yet' : 'connect a channel to see its cost') : null} muted={adSpend == null} />
          </div>

          {data.email_engagement && (data.email_engagement.totals?.sent || data.email_engagement.totals?.delivered) ? (
            <div className="mk-card">
              <div className="mk-card-title">Email engagement<span className="mk-dim">{data.email_engagement.basis}</span></div>
              <div className="mk-tiles" style={{ marginBottom: 12 }}>
                <Tile label="Sent" value={num(data.email_engagement.totals.sent)} />
                <Tile label="Delivered" value={num(data.email_engagement.totals.delivered)} sub={data.email_engagement.totals.sent ? `${Math.round((data.email_engagement.totals.delivered / data.email_engagement.totals.sent) * 100)}% of sent` : null} />
                <Tile label="Opened" value={num(data.email_engagement.totals.opened)} sub={data.email_engagement.totals.delivered ? `${Math.round((data.email_engagement.totals.opened / data.email_engagement.totals.delivered) * 100)}% of delivered` : null} />
                <Tile label="Clicked" value={num(data.email_engagement.totals.clicked)} />
                <Tile label="Bounced" value={num(data.email_engagement.totals.bounced)} sub={data.email_engagement.totals.complained ? `${num(data.email_engagement.totals.complained)} complained` : null} muted={!data.email_engagement.totals.bounced} />
              </div>
              {data.email_engagement.campaigns?.length ? (
                <div className="mk-table-wrap"><table className="mk-table"><thead><tr><th>Campaign</th><th className="num">Sent</th><th className="num">Delivered</th><th className="num">Opened</th><th className="num">Clicked</th><th className="num">Bounced</th><th className="num">Complained</th><th className="num">Unsubscribed</th></tr></thead>
                  <tbody>{data.email_engagement.campaigns.map((c) => (
                    <tr key={c.campaign_id}><td>{c.campaign}</td><td className="num">{num(c.sent)}</td><td className="num">{num(c.delivered)}</td><td className="num">{num(c.opened)}</td><td className="num">{num(c.clicked)}</td><td className="num">{num(c.bounced)}</td><td className="num">{num(c.complained)}</td><td className="num">{num(c.unsubscribed)}</td></tr>
                  ))}</tbody></table></div>
              ) : null}
            </div>
          ) : null}

          <SearchPerformance gsc={data.search_console} ga4={data.ga4} />

          <div className="mk-card">
            <div className="mk-card-title">By channel<span className="mk-dim">{shown.length} with activity · {range?.start ? `${fmtDay(range.start)} → ${range.end ? fmtDay(range.end) : 'now'}` : 'all time'}</span></div>
            {shown.length === 0 ? (
              <div className="mk-muted" style={{ padding: '8px 0 12px' }}>No attributed leads in this window.</div>
            ) : (
            <div className="mk-table-wrap">
            <table className="mk-table">
              <thead><tr>
                <th>Channel</th><th className="num">Leads</th><th className="num">Booked</th><th>Sold by</th><th className="num">{primaryLabel}</th>{subscriptionBasis && <th className="num">Booked revenue</th>}<th className="num">Cost</th><th className="num">Cost / lead</th>
              </tr></thead>
              <tbody>
                {shown.map((c) => {
                  const hasData = c.rows.length > 0;
                  const low = c.precision === 'low';
                  const isOpen = !!open[c.key];
                  const costPerLead = c.cost != null && c.leads > 0 ? c.cost / c.leads : null;
                  const primaryVal = subscriptionBasis ? c.subscription : c.revenue;
                  return (
                    <React.Fragment key={c.key}>
                      <tr className={`mk-row ${low ? 'is-low' : ''}`} title={low ? c.basis : undefined}>
                        <td>
                          <div className="mk-ch">
                            <button className="mk-ch-name" onClick={() => hasData && setOpen((o) => ({ ...o, [c.key]: !o[c.key] }))} disabled={!hasData} aria-expanded={isOpen}>
                              <span className="mk-caret" aria-hidden>{hasData ? (isOpen ? '▾' : '▸') : ''}</span>{c.label}
                            </button>
                            {!hasData && c.connected && <Chip>connected · no leads yet</Chip>}
                            {c.precision === 'medium' && hasData && <Chip title={c.basis}>inferred</Chip>}
                            {low && hasData && <Chip title={c.basis}>{c.basis || 'low precision'}</Chip>}
                          </div>
                          {c.tied && (
                            <div className={`mk-tied ${c.tied.status !== 'connected' ? 'is-error' : ''}`}>
                              {c.tied.status !== 'connected' && <span className="mk-err-dot" aria-hidden />}
                              {c.tied.account ? `account ${c.tied.account}` : c.tied.pixel_id ? `pixel ${c.tied.pixel_id}` : c.tied.status}
                              {c.tied.status !== 'connected' ? ` · connection error (${c.tied.status})` : ''}{c.tied.managed_by_abon ? ' · managed by Abon' : ''}
                            </div>
                          )}
                        </td>
                        <td className="num">{hasData ? <button className="mk-link" onClick={() => openLeads(c.rows.flatMap((r) => r.lead_ids || []))}>{num(c.leads)}</button> : '—'}</td>
                        <td className="num">{hasData ? num(c.booked) : '—'}</td>
                        <td>{hasData ? <SoldBy reps={c.reps} /> : '—'}</td>
                        <td className="num">{hasData ? <button className="mk-link" onClick={() => openLeads(c.rows.filter((r) => Number(subscriptionBasis ? r.subscription_value : r.revenue) > 0).flatMap((r) => r.lead_ids || []))}>{money(primaryVal)}</button> : '—'}</td>
                        {subscriptionBasis && <td className="num">{hasData ? money(c.revenue) : '—'}</td>}
                        <td className="num">{c.cost != null ? money(c.cost) : '—'}</td>
                        <td className="num">{costPerLead != null ? money(costPerLead) : '—'}</td>
                      </tr>
                      {isOpen && c.rows.map((r, i) => (
                        <tr key={`${c.key}-${i}`} className={`mk-subrow ${r.precision === 'low' ? 'is-low' : ''}`} title={r.precision === 'low' ? r.attribution_basis : undefined}>
                          <td><div className="mk-sub-name">{r.campaign && r.campaign !== '(none)' ? r.campaign : r.source || '—'}{r.keyword && r.keyword !== '(none)' ? <span className="mk-kw"> · {r.keyword}</span> : null}</div></td>
                          <td className="num"><button className="mk-link" onClick={() => openLeads(r.lead_ids)}>{num(r.leads)}</button></td>
                          <td className="num">{num(r.booked_appointments)}</td>
                          <td><SoldBy reps={r.reps} /></td>
                          <td className="num">{money(subscriptionBasis ? r.subscription_value : r.revenue)}</td>
                          {subscriptionBasis && <td className="num">{money(r.revenue)}</td>}
                          <td className="num">{(r.cost ?? r.spend) != null ? money(r.cost ?? r.spend) : '—'}</td>
                          <td className="num">{(r.cost ?? r.spend) != null && Number(r.leads) > 0 ? money(Number(r.cost ?? r.spend) / Number(r.leads)) : '—'}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            </div>
            )}
            {offChannels.length > 0 && (
              <div className="mk-off"><b>Not connected</b>{offChannels.map((c) => <span key={c.key}>{c.label}</span>)}<span className="mk-dim">· ad spend and cost per lead appear once an ad account is tied to this workspace</span></div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
